import { useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import {
  Card,
  Button,
  Input,
  Modal,
  Table,
  StatusBadge,
  Badge,
  ConfidenceBadge,
} from '@/components/ui'
import { EmbedCodeGenerator } from '@/components/canary'
import { EmptyCanaryDetail } from '@/components/onboarding'
import { formatDate, formatListTimestamp } from '@/utils/format-date'
import type { AuthorizedDomain, CanaryRecipient, DetectionResponse } from '@spoofio/shared/types'

export function CanaryDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { orgId } = useCurrentOrgId()

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false)
  const [isAddRecipientModalOpen, setIsAddRecipientModalOpen] = useState(false)
  const [showToken, setShowToken] = useState(false)

  const { data: canary, isLoading, error } = useQuery({
    queryKey: ['canary', id],
    queryFn: () => api.getCanary(id),
    enabled: !!id,
  })

  const { data: detectionsData } = useQuery({
    queryKey: ['canary-detections', id],
    queryFn: () =>
      api.listDetections({ canaryId: id, limit: 10, sortOrder: 'desc' }),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteCanary(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canaries'] })
      navigate('/canaries')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string; isActive?: boolean; confidenceThreshold?: number; screenshotEnabled?: boolean }) =>
      api.updateCanary(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary', id] })
      setIsEditModalOpen(false)
    },
  })

  const addDomainMutation = useMutation({
    mutationFn: (data: { fqdn: string; isDevelopment?: boolean }) =>
      api.addAuthorizedDomain(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary', id] })
      setIsAddDomainModalOpen(false)
    },
  })

  const removeDomainMutation = useMutation({
    mutationFn: (domainId: string) => api.removeAuthorizedDomain(domainId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary', id] })
    },
  })

  const [editingDomainId, setEditingDomainId] = useState<string | null>(null)

  const updateDomainMutation = useMutation({
    mutationFn: ({ domainId, fqdn }: { domainId: string; fqdn: string }) =>
      api.updateAuthorizedDomain(domainId, { fqdn }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['canary', id] })
      setEditingDomainId(null)
    },
  })

  const { data: recipients } = useQuery({
    queryKey: ['canary-recipients', id],
    queryFn: () => api.listCanaryRecipients(id),
    enabled: !!id,
  })

  const addRecipientMutation = useMutation({
    mutationFn: (data: { email: string }) => api.addCanaryRecipient(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary-recipients', id] })
      setIsAddRecipientModalOpen(false)
    },
  })

  const removeRecipientMutation = useMutation({
    mutationFn: (recipientId: string) => api.removeCanaryRecipient(recipientId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary-recipients', id] })
    },
  })

  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.getUserProfile(),
  })

  const { data: billingStatus } = useQuery({
    queryKey: ['billing-status', orgId],
    queryFn: () => api.getBillingStatus(orgId ?? ''),
    enabled: !!orgId,
  })

  const ffiecMutation = useMutation({
    mutationFn: (enabled: boolean) => {
      const orgIdStr = canary?.organizationId ?? ''
      return enabled
        ? api.enableFfiecCanary(orgIdStr, id)
        : api.disableFfiecCanary(orgIdStr, id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['canary', id] })
    },
  })

  const { mutate: mutateFfiec } = ffiecMutation

  const handleFfiecToggle = useCallback((enabled: boolean) => {
    mutateFfiec(enabled)
  }, [mutateFfiec])

  const isPlatformAdmin = profile?.isPlatformAdmin ?? false
  const canFfiec = isPlatformAdmin || (billingStatus?.plan.ffiecEnhancedCanaries ?? 0) > 0

  if (isLoading) {
    return <CanaryDetailSkeleton />
  }

  if (error || !canary) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load canary</p>
        <Button variant="secondary" className="mt-4" onClick={() => { navigate('/canaries'); }}>
          Back to Canaries
        </Button>
      </div>
    )
  }

  const detectionColumns = [
    {
      key: 'fakeFqdn',
      header: 'Fake Domain',
      render: (d: DetectionResponse) => (
        <span className="font-mono text-sm">{d.fakeFqdn}</span>
      ),
    },
    {
      key: 'confidenceScore',
      header: 'Confidence',
      render: (d: DetectionResponse) => <ConfidenceBadge score={d.confidenceScore} />,
    },
    {
      key: 'cfCountry',
      header: 'Location',
      render: (d: DetectionResponse) =>
        d.cfCountry ? `${d.cfCity ?? ''} ${d.cfCountry}`.trim() : 'Unknown',
    },
    {
      key: 'detectedAt',
      header: 'Time',
      render: (d: DetectionResponse) => formatListTimestamp(d.detectedAt),
    },
    {
      key: 'alertSent',
      header: 'Alert',
      render: (d: DetectionResponse) => (
        <Badge variant={d.alertSent ? 'success' : 'gray'}>
          {d.alertSent ? 'Sent' : 'No'}
        </Badge>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/canaries" className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{canary.name}</h1>
              <StatusBadge isActive={canary.isActive} />
            </div>
            {canary.description && (
              <p className="text-gray-500 mt-1">{canary.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => { setIsEditModalOpen(true); }}>
            Edit
          </Button>
          <Button variant="danger" onClick={() => { setIsDeleteModalOpen(true); }}>
            Delete
          </Button>
        </div>
      </div>

      {/* Embed Code Generator */}
      <EmbedCodeGenerator token={canary.token} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Settings */}
        <Card title="Settings">
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Confidence Threshold</dt>
              <dd className="text-sm font-medium">{canary.confidenceThreshold}%</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Screenshots</dt>
              <dd className="text-sm font-medium">
                {canary.screenshotEnabled ? 'Enabled' : 'Disabled'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Created</dt>
              <dd className="text-sm font-medium">{formatDate(canary.createdAt)}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Token</dt>
              <dd className="flex items-center gap-2">
                <span className="text-sm font-mono">
                  {showToken ? canary.token : `${canary.token.slice(0, 8)}${'•'.repeat(16)}`}
                </span>
                <button
                  type="button"
                  onClick={() => { setShowToken(!showToken); }}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  {showToken ? 'Hide' : 'Show'}
                </button>
              </dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* Authorized Domains */}
      <Card
        title="Authorized Domains"
        description="Domains where this canary can legitimately load"
        actions={
          <Button variant="secondary" size="sm" onClick={() => { setIsAddDomainModalOpen(true); }}>
            Add Domain
          </Button>
        }
      >
        <AuthorizedDomainsList
          domains={canary.authorizedDomains}
          onRemove={(domainId) => { removeDomainMutation.mutate(domainId); }}
          isRemoving={removeDomainMutation.isPending}
          onUpdate={(domainId, fqdn) => { updateDomainMutation.mutate({ domainId, fqdn }); }}
          isUpdating={updateDomainMutation.isPending}
          updateError={updateDomainMutation.error?.message}
          editingDomainId={editingDomainId}
          onEditStart={(domainId) => { setEditingDomainId(domainId); }}
          onEditCancel={() => { setEditingDomainId(null); }}
        />
      </Card>

      {/* Alert Recipients */}
      <Card
        title="Alert Recipients"
        description="Canary-specific recipients override organization defaults. If none are set, organization defaults are used."
        actions={
          <Button variant="secondary" size="sm" onClick={() => { setIsAddRecipientModalOpen(true); }}>
            Add Recipient
          </Button>
        }
      >
        <CanaryRecipientsList
          recipients={recipients ?? []}
          onRemove={(recipientId) => { removeRecipientMutation.mutate(recipientId); }}
          isRemoving={removeRecipientMutation.isPending}
        />
      </Card>

      {/* Recent Detections */}
      {(detectionsData?.data ?? []).length === 0 ? (
        <EmptyCanaryDetail canaryId={id} />
      ) : (
        <Card
          title="Recent Detections"
          actions={
            <Link
              to={`/detections?canaryId=${id}`}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          }
        >
          <Table
            columns={detectionColumns}
            data={detectionsData?.data ?? []}
            keyExtractor={(d) => d.id}
            onRowClick={(d) => { navigate(`/detections/${d.id}`); }}
            emptyMessage="No detections yet"
          />
        </Card>
      )}

      {/* Edit Modal */}
      <EditCanaryModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); }}
        canary={canary}
        onSave={(data) => { updateMutation.mutate(data); }}
        isLoading={updateMutation.isPending}
        canFfiec={canFfiec}
        ffiecEnhancedEnabled={canary.ffiecEnhancedEnabled}
        onFfiecToggle={handleFfiecToggle}
        isFfiecLoading={ffiecMutation.isPending}
        ffiecError={ffiecMutation.isError ? (ffiecMutation.error.message || 'Failed to update FFIEC Enhanced Detection') : undefined}
      />

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); }}
        title="Delete Canary"
        footer={
          <>
            <Button
              variant="danger"
              onClick={() => { deleteMutation.mutate(); }}
              isLoading={deleteMutation.isPending}
            >
              Delete
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setIsDeleteModalOpen(false); }}
              className="mr-3"
            >
              Cancel
            </Button>
          </>
        }
      >
        <p className="text-gray-500">
          Are you sure you want to delete this canary? All detections and alerts
          will also be deleted. This action cannot be undone.
        </p>
      </Modal>

      {/* Add Domain Modal */}
      <AddDomainModal
        isOpen={isAddDomainModalOpen}
        onClose={() => { setIsAddDomainModalOpen(false); }}
        onAdd={(data) => { addDomainMutation.mutate(data); }}
        isLoading={addDomainMutation.isPending}
        error={addDomainMutation.error?.message}
      />

      {/* Add Recipient Modal */}
      <AddCanaryRecipientModal
        isOpen={isAddRecipientModalOpen}
        onClose={() => { setIsAddRecipientModalOpen(false); }}
        onAdd={(data) => { addRecipientMutation.mutate(data); }}
        isLoading={addRecipientMutation.isPending}
        error={addRecipientMutation.error?.message}
      />
    </div>
  )
}

function AuthorizedDomainsList({
  domains,
  onRemove,
  isRemoving,
  onUpdate,
  isUpdating,
  updateError,
  editingDomainId,
  onEditStart,
  onEditCancel,
}: {
  domains: AuthorizedDomain[]
  onRemove: (id: string) => void
  isRemoving: boolean
  onUpdate: (domainId: string, fqdn: string) => void
  isUpdating: boolean
  updateError?: string
  editingDomainId: string | null
  onEditStart: (domainId: string) => void
  onEditCancel: () => void
}) {
  const [editFqdn, setEditFqdn] = useState('')

  const startEditing = (domain: AuthorizedDomain) => {
    onEditStart(domain.id)
    setEditFqdn(domain.fqdn)
  }

  const cancelEditing = () => {
    onEditCancel()
    setEditFqdn('')
  }

  const saveEdit = () => {
    if (editingDomainId && editFqdn.trim()) {
      onUpdate(editingDomainId, editFqdn.toLowerCase().trim())
    }
  }

  if (domains.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">
        No authorized domains configured
      </p>
    )
  }

  return (
    <ul className="divide-y divide-gray-200">
      {domains.map((domain) => (
        <li key={domain.id} className="flex items-center justify-between py-3">
          {editingDomainId === domain.id ? (
            <div className="flex items-center gap-2 flex-1 mr-2">
              <Input
                value={editFqdn}
                onChange={(e) => { setEditFqdn(e.target.value); }}
                className="flex-1"
                error={updateError}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit()
                  if (e.key === 'Escape') cancelEditing()
                }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={saveEdit}
                disabled={isUpdating || !editFqdn.trim()}
                isLoading={isUpdating}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={cancelEditing}
                disabled={isUpdating}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{domain.fqdn}</span>
                {domain.isDevelopment && (
                  <Badge variant="gray">Dev</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { startEditing(domain); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { onRemove(domain.id); }}
                  disabled={isRemoving}
                  className="text-danger-600 hover:text-danger-700"
                >
                  Remove
                </Button>
              </div>
            </>
          )}
        </li>
      ))}
    </ul>
  )
}

function EditCanaryModal({
  isOpen,
  onClose,
  canary,
  onSave,
  isLoading,
  canFfiec,
  ffiecEnhancedEnabled,
  onFfiecToggle,
  isFfiecLoading,
  ffiecError,
}: {
  isOpen: boolean
  onClose: () => void
  canary: { name: string; description?: string; isActive: boolean; confidenceThreshold: number; screenshotEnabled: boolean; ffiecEnhancedEnabled: boolean }
  onSave: (data: { name?: string; description?: string; isActive?: boolean; confidenceThreshold?: number; screenshotEnabled?: boolean }) => void
  isLoading: boolean
  canFfiec: boolean
  ffiecEnhancedEnabled: boolean
  onFfiecToggle: (enabled: boolean) => void
  isFfiecLoading: boolean
  ffiecError?: string
}) {
  const [name, setName] = useState(canary.name)
  const [description, setDescription] = useState(canary.description ?? '')
  const [isActive, setIsActive] = useState(canary.isActive)
  const [threshold, setThreshold] = useState(canary.confidenceThreshold)
  const [screenshotEnabled, setScreenshotEnabled] = useState(canary.screenshotEnabled)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      name: name !== canary.name ? name : undefined,
      description: description !== (canary.description ?? '') ? description : undefined,
      isActive: isActive !== canary.isActive ? isActive : undefined,
      confidenceThreshold: threshold !== canary.confidenceThreshold ? threshold : undefined,
      screenshotEnabled: screenshotEnabled !== canary.screenshotEnabled ? screenshotEnabled : undefined,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Canary"
      footer={
        <>
          <Button type="submit" form="edit-canary-form" isLoading={isLoading}>
            Save Changes
          </Button>
          <Button variant="secondary" onClick={onClose} className="mr-3">
            Cancel
          </Button>
        </>
      }
    >
      <form id="edit-canary-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => { setName(e.target.value); }}
          required
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => { setDescription(e.target.value); }}
        />
        <div>
          <label className="label">Confidence Threshold</label>
          <input
            type="range"
            min="0"
            max="100"
            value={threshold}
            onChange={(e) => { setThreshold(Number(e.target.value)); }}
            className="w-full"
          />
          <p className="text-sm text-gray-500">
            Alerts will be sent for detections with confidence {threshold}% or higher
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="screenshotEnabled"
            checked={screenshotEnabled}
            onChange={(e) => { setScreenshotEnabled(e.target.checked); }}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="screenshotEnabled" className="text-sm text-gray-700">
            Capture screenshots of fake pages when alerts are sent
          </label>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ffiecEnhancedEnabled"
              checked={ffiecEnhancedEnabled}
              disabled={!canFfiec || isFfiecLoading}
              onChange={(e) => { onFfiecToggle(e.target.checked); }}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
            />
            <label htmlFor="ffiecEnhancedEnabled" className="text-sm text-gray-700">
              FFIEC Enhanced Detection — enriches alert emails with hosting intelligence, WHOIS data, and takedown guidance
            </label>
          </div>
          {!canFfiec && (
            <p className="mt-1 text-xs text-gray-500">
              Requires the FFIEC add-on ($50/mo per canary).{' '}
              <Link to="/billing" className="text-primary-600 hover:text-primary-700">
                Upgrade on the Billing page.
              </Link>
            </p>
          )}
          {ffiecError && (
            <p className="mt-1 text-xs text-danger-600">{ffiecError}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            checked={isActive}
            onChange={(e) => { setIsActive(e.target.checked); }}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="text-sm text-gray-700">
            Active (canary will trigger detections)
          </label>
        </div>
      </form>
    </Modal>
  )
}

function AddDomainModal({
  isOpen,
  onClose,
  onAdd,
  isLoading,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  onAdd: (data: { fqdn: string; isDevelopment?: boolean }) => void
  isLoading: boolean
  error?: string
}) {
  const [fqdn, setFqdn] = useState('')
  const [isDevelopment, setIsDevelopment] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onAdd({ fqdn: fqdn.toLowerCase().trim(), isDevelopment })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Authorized Domain"
      footer={
        <>
          <Button type="submit" form="add-domain-form" isLoading={isLoading}>
            Add Domain
          </Button>
          <Button variant="secondary" onClick={onClose} className="mr-3">
            Cancel
          </Button>
        </>
      }
    >
      <form id="add-domain-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Domain (FQDN)"
          placeholder="example.com"
          value={fqdn}
          onChange={(e) => { setFqdn(e.target.value); }}
          helperText="Enter the fully qualified domain name without protocol (e.g., example.com, not https://example.com)"
          error={error}
          required
        />
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isDevelopment"
            checked={isDevelopment}
            onChange={(e) => { setIsDevelopment(e.target.checked); }}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="isDevelopment" className="text-sm text-gray-700">
            Development environment (localhost, staging, etc.)
          </label>
        </div>
      </form>
    </Modal>
  )
}

function CanaryRecipientsList({
  recipients,
  onRemove,
  isRemoving,
}: {
  recipients: CanaryRecipient[]
  onRemove: (id: string) => void
  isRemoving: boolean
}) {
  if (recipients.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">
        No canary-specific recipients configured. Organization defaults will be used.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-gray-200">
      {recipients.map((recipient) => (
        <li key={recipient.id} className="flex items-center justify-between py-3">
          <span className="text-sm">{recipient.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onRemove(recipient.id); }}
            disabled={isRemoving}
            className="text-danger-600 hover:text-danger-700"
          >
            Remove
          </Button>
        </li>
      ))}
    </ul>
  )
}

function AddCanaryRecipientModal({
  isOpen,
  onClose,
  onAdd,
  isLoading,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  onAdd: (data: { email: string }) => void
  isLoading: boolean
  error?: string
}) {
  const [email, setEmail] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onAdd({ email: email.trim() })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Alert Recipient"
      footer={
        <>
          <Button type="submit" form="add-recipient-form" isLoading={isLoading}>
            Add Recipient
          </Button>
          <Button variant="secondary" onClick={onClose} className="mr-3">
            Cancel
          </Button>
        </>
      }
    >
      <form id="add-recipient-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          placeholder="security@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
          helperText="This recipient will override organization defaults for this canary"
          error={error}
          required
        />
      </form>
    </Modal>
  )
}

function CanaryDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 bg-gray-200 rounded w-64 animate-pulse" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="h-24 bg-gray-100 rounded" />
        </div>
        <div className="bg-white shadow rounded-lg p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-6 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

